import React, { useState, useEffect, useRef, useMemo } from 'react';
import Select from 'react-select';
import { formatToPakistanTime } from '../utils/dateUtils';
import '../styles/MistakesTab.css';
import ConfirmDialog from './ConfirmDialog';
import DraggableImagePanel from './DraggableImagePanel';
import DateFilterCalendar from './DateFilterCalendar';

const API_BASE_URL = window.API_BASE_URL;

const CAN_MARK_AI_ROLES = ['teacher', 'examiner', 'administrator'];

function recordIsAiMistake(record) {
  return recordCause(record) === 'ai';
}

function recordCause(record) {
  const c = String(record && record.mistake_cause || '').trim().toLowerCase();
  if (c === 'ai' || c === 'scan' || c === 'sheet' || c === 'student') return c;
  const v = record && record.is_ai_mistake;
  if (v === true || v === 1 || v === '1') return 'ai';
  return 'student';
}

const CAUSE_MARKS = [
  { cause: 'student', label: 'Student', icon: 'fa-user',        titleOn: 'Student mistake (default)', titleOff: 'Mark as a student mistake' },
  { cause: 'ai',      label: 'AI',      icon: 'fa-robot',       titleOn: 'AI mistake', titleOff: 'Mark as AI mistake' },
  { cause: 'scan',    label: 'Scan',    icon: 'fa-camera',      titleOn: 'Scan mistake', titleOff: 'Mark as a scan mistake' },
  { cause: 'sheet',   label: 'Sheet',   icon: 'fa-file-lines',  titleOn: 'Sheet mistake', titleOff: 'Mark as a sheet mistake' }
];

const CAUSE_BADGE = {
  student: { className: 'cause-badge--student', text: 'Student mistake' },
  ai:      { className: 'cause-badge--ai',      text: 'AI mistake' },
  scan:    { className: 'cause-badge--scan',    text: 'Scan mistake' },
  sheet:   { className: 'cause-badge--sheet',   text: 'Sheet mistake' }
};

function toLocalDayStr(val) {
  if (!val) return null;
  if (typeof val === 'string') {
    const iso = val.match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
  }
  const d = new Date(val);
  if (isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

function isMcqRecord(r) {
  if (r && r.folder_type === 'mcq') return true;
  if (typeof r.subject === 'string' && /-[a-j]$/i.test(r.subject.trim())) return true;
  const comments = String(r.comments || '');
  return /MCQ roll cross-check/i.test(comments)
    || /Approve & Match:/i.test(comments)
    || /Bubbled Roll/i.test(comments)
    || /\[mcq\]/i.test(comments);
}

// One scans-style folder per class + subject + test date + teacher +
// MCQ/subjective, so "9A+9B-CHEMISTRY" and "9A+9B-CHEMISTRY MCQs" on the
// same day stay separate (Scans already treats them as two folders).
function mistakeFolderKey(r) {
  const cls = String(r.class || '').trim().toLowerCase();
  const subj = String(r.subject || '').trim().toLowerCase();
  const date = toLocalDayStr(r.test_date) || '';
  const teacher = String(r.teacher_name || '').trim().toLowerCase();
  const kind = isMcqRecord(r) ? 'mcq' : 'subj';
  return `${cls}||${subj}||${date}||${teacher}||${kind}`;
}

function mistakeFolderTitle(r) {
  const cls = String(r.class || '').trim() || 'Unknown';
  const subj = String(r.subject || '').trim() || 'Unknown';
  const base = `${cls}-${subj}`;
  return isMcqRecord(r) ? `${base} MCQs` : base;
}

function formatFolderDate(dateStr) {
  if (!dateStr) return 'No date';
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? `${dateStr}T00:00:00` : dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function recordOwnedByTeacher(record, user) {
  const owner = String(record && record.teacher_name || '').trim().toLowerCase();
  if (!owner || !user) return false;
  return [user.username, user.name, user.full_name]
    .filter(Boolean)
    .some(n => String(n).trim().toLowerCase() === owner);
}

// Pull the roster/expected name out of comments written by Sort-tab name
// corrections: `AI read: "…" · Expected: "…"`.
function extractExpectedName(comments) {
  if (!comments) return null;
  const m = String(comments).match(/Expected:\s*"([^"]+)"/i);
  return m ? m[1].trim() : null;
}

function extractRoll(name) {
  const m = String(name || '').match(/\(([A-Za-z0-9]+)\)/);
  return m ? m[1].toUpperCase() : '';
}

function formatExpectedSide(readName, expected) {
  if (!expected) return '';
  const roll = extractRoll(readName);
  if (roll && !/\([A-Za-z0-9]+\)/.test(expected)) {
    return `(${roll}) ${expected}`;
  }
  return expected;
}

function folderCourseName(folder) {
  if (!folder) return '';
  const cls = String(folder.class || '').trim();
  const subj = String(folder.subject || '').replace(/\bMCQs?\b/gi, '').trim();
  if (cls && subj) return `${cls} - ${subj}`;
  return cls || subj || String(folder.title || '').replace(/\bMCQs?\b/gi, '').trim();
}

function pickMistakeRecords(visible, selectedIds) {
  const scoped = selectedIds && selectedIds.size
    ? visible.filter(r => selectedIds.has(r.id))
    : visible;
  // EduPage only gets student name mistakes — never AI, scan, or sheet.
  const student = scoped.filter(r => recordCause(r) === 'student');
  const withExpected = student.filter(r => {
    const expected = extractExpectedName(r.comments);
    const read = (r.student_name || '').trim();
    return expected && expected !== read;
  });
  return withExpected.length ? withExpected : student;
}

function buildMistakePayload(records) {
  const lines = [];
  const rolls = [];
  for (const r of records) {
    const readName = (r.student_name || '').trim();
    if (!readName) continue;
    const expected = extractExpectedName(r.comments);
    const right = formatExpectedSide(readName, expected);
    lines.push(right && right !== readName ? `${readName}->${right}` : (right ? `${readName}->${right}` : readName));
    const roll = extractRoll(readName);
    if (roll) rolls.push(roll);
  }
  return { lines, rolls: [...new Set(rolls)] };
}

function formatEduMistakeMessage(folder, lines) {
  const cls = folder && String(folder.class || '').trim();
  const subj = folder && String(folder.subject || '').replace(/\bMCQs?\b/gi, '').trim();
  const date = folder && folder.test_date ? formatFolderDate(folder.test_date) : '';
  const where = [cls, subj].filter(Boolean).join(' · ');
  const header = [
    'Assalam o Alaikum,',
    '',
    'Following are the mistakes in student names written on the answer sheet.',
    'The name on the LEFT is how it was written on the sheet. The name on the RIGHT is the correct name from the class list.',
    'Please check your name and write it clearly next time.',
    '',
    where ? `Class / subject: ${where}` : null,
    date ? `Test date: ${date}` : null,
    '',
    'Mistakes:',
  ].filter((l) => l !== null);
  return `${header.join('\n')}\n${lines.join('\n')}`;
}

// Mark indices whose non-whitespace chars disagree (case-insensitive).
// Spaces are skipped so "(B81)AHMAD ALT" aligns with "(B81) Ahmad Ali"
// and only T↔i light up — not every letter after a spacing difference.
function mismatchCharIndices(left, right) {
  const leftBad = new Set();
  const rightBad = new Set();
  const leftMap = [];
  const rightMap = [];
  for (let i = 0; i < left.length; i++) {
    if (!/\s/.test(left[i])) leftMap.push(i);
  }
  for (let i = 0; i < right.length; i++) {
    if (!/\s/.test(right[i])) rightMap.push(i);
  }
  const n = Math.max(leftMap.length, rightMap.length);
  for (let i = 0; i < n; i++) {
    if (i >= leftMap.length) {
      rightBad.add(rightMap[i]);
    } else if (i >= rightMap.length) {
      leftBad.add(leftMap[i]);
    } else if (left[leftMap[i]].toLowerCase() !== right[rightMap[i]].toLowerCase()) {
      leftBad.add(leftMap[i]);
      rightBad.add(rightMap[i]);
    }
  }
  return { leftBad, rightBad };
}

function renderHighlightedChars(text, badIndices) {
  return Array.from(text).map((ch, i) => (
    badIndices.has(i)
      ? <mark key={i} className="name-diff-mismatch">{ch}</mark>
      : <span key={i}>{ch}</span>
  ));
}

// Card header line: "AI read->Expected" with wrong letters/digits marked
// on both sides when they differ.
function NameDiffLine({ readName, expected }) {
  if (!readName && !expected) return null;
  if (!expected || expected === readName) {
    return <div className="card-name-line">{readName || expected}</div>;
  }
  const { leftBad, rightBad } = mismatchCharIndices(readName, expected);
  return (
    <div className="card-name-line" title={`${readName} → ${expected}`}>
      {renderHighlightedChars(readName, leftBad)}
      <span className="name-diff-arrow">-&gt;</span>
      {renderHighlightedChars(expected, rightBad)}
    </div>
  );
}

// Card thumbnail: loads via the shared Mistakes URL cache and crops so only
// the top 20% of the page (name/roll header) is visible — same crop idea as
// Sort tab's top20 focus, scaled down for the card face.
function InlineMistakeImage({ imageRef, cacheRef, cacheVersion, fetchImageUrl, onOpen }) {
  const key = imageRef ? `${imageRef.kind}:${imageRef.value}` : null;
  const entry = key ? cacheRef.current.get(key) : null;
  const url = entry && entry.url;
  const [cropH, setCropH] = useState(64);

  useEffect(() => {
    if (imageRef) fetchImageUrl(imageRef);
  }, [key, fetchImageUrl]); // eslint-disable-line react-hooks/exhaustive-deps -- imageRef identity changes each render; key is stable

  if (!imageRef) {
    return (
      <div className="card-image-preview card-image-preview--empty">
        No image
      </div>
    );
  }

  if (!url) {
    return (
      <div className="card-image-preview card-image-preview--empty">
        {entry && entry.error ? 'Image unavailable' : 'Loading…'}
      </div>
    );
  }

  return (
    <div
      className="card-image-preview"
      style={{ height: cropH }}
      onClick={onOpen}
      title="View scanned image (top 20%)"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <img
        src={url}
        alt=""
        draggable={false}
        onLoad={(e) => {
          const el = e.currentTarget;
          if (!el.naturalWidth) return;
          const renderedH = el.naturalHeight * (el.clientWidth / el.naturalWidth);
          if (renderedH > 0) setCropH(Math.max(48, Math.round(renderedH * 0.2)));
        }}
      />
    </div>
  );
}

export default function MistakesTab({ currentUser }) {
  const [unmatchedRecords, setUnmatchedRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Filters — three independent date dimensions:
  //   • selectedDate (single day picked in the 30-day calendar)
  //   • filterStartDate / filterEndDate (custom range card below the calendar)
  // and three categorical multi-selects (Teacher / Class / Subject) in the
  // top header strip. "Teacher" is the scans-folder owner (whose test this
  // is), not the examiner who logged the unmatched row (created_by).
  const [selectedDate, setSelectedDate] = useState('');
  const [filterMode, setFilterMode] = useState('none');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterStudent, setFilterStudent] = useState(''); // Case-insensitive substring match on student_name
  const [filterRubricType, setFilterRubricType] = useState('all'); // 'all' | 'mcq' | 'subjective'
  const [filterCause, setFilterCause] = useState('all'); // 'all' | 'student' | 'ai' | 'scan' | 'sheet'
  const [filterTeachers, setFilterTeachers] = useState([]); // Multi-select: [{value, label}]
  const [filterClasses, setFilterClasses] = useState([]); // Multi-select: [{value, label}]
  const [filterSubjects, setFilterSubjects] = useState([]); // Multi-select: [{value, label}]
  const [allRecords, setAllRecords] = useState([]); // All records before client-side filtering
  const [teacherFilterInput, setTeacherFilterInput] = useState('');
  const [classFilterInput, setClassFilterInput] = useState('');
  const [subjectFilterInput, setSubjectFilterInput] = useState('');

  const [uniqueTeachers, setUniqueTeachers] = useState([]);
  const [uniqueClasses, setUniqueClasses] = useState([]);
  const [uniqueSubjects, setUniqueSubjects] = useState([]);
  const [confirm, setConfirm] = useState({ show: false, text: '', onConfirm: null });
  const [previewImage, setPreviewImage] = useState(null); // { url, name, recordId }
  const [previewLoading, setPreviewLoading] = useState(null); // filename being loaded
  const [showTopOffendersModal, setShowTopOffendersModal] = useState(false);
  // Collapses the left rail (calendar + date range + type toggles + Top 20)
  // so the records grid can use the full width. Mirrors AnalyzeTab /
  // StudentUploader "Hide folders" UX.
  const [calendarCollapsed, setCalendarCollapsed] = useState(false);
  const [markingAiIds, setMarkingAiIds] = useState(() => new Set());
  // null = folder list (8T+8U-english-a cards). Set to a mistakeFolderKey
  // when the teacher drills into one folder's records.
  const [selectedFolderKey, setSelectedFolderKey] = useState(null);
  const [eduBridgeReady, setEduBridgeReady] = useState(false);
  const [eduNotice, setEduNotice] = useState('');
  const eduWaitRef = useRef(null);
  const eduPendingRef = useRef(null);

  const canMarkAi = CAN_MARK_AI_ROLES.includes(currentUser?.role);
  const isTeacher = currentUser?.role === 'teacher';

  // Multi-select for bulk delete. Backed by a Set keyed by record.id. The
  // toggle / clear helpers below take a single id; a "Select all visible"
  // checkbox in the header strip drives bulk selection across the current
  // filtered set. Cleared automatically when the filter changes so the
  // grader doesn't accidentally delete records that are no longer on
  // screen.
  const [selectedRecordIds, setSelectedRecordIds] = useState(() => new Set());
  const toggleRecordSelected = (id) => {
    setSelectedRecordIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedRecordIds(new Set());

  // Load unmatched records — fetches the full dataset once on mount.
  // Date / class / subject filtering is applied client-side so the
  // 30-day calendar can update instantly without round-tripping the API.
  const loadUnmatchedRecords = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('authToken');

      const response = await fetch(`${API_BASE_URL}/unmatched-students`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error('Failed to fetch unmatched records: ' + response.status);
      }

      let data = await response.json();

      // Ensure data is an array
      if (!Array.isArray(data)) {
        console.error('API response is not an array:', data);
        data = [];
      }

      // Strip PERMANENT: prefix from comments for display. Normalize the
      // AI-mistake flag so TINYINT 0/1 from MySQL is a real boolean.
      const processedData = data.map(record => ({
        ...record,
        mistake_cause: recordCause(record),
        is_ai_mistake: recordCause(record) === 'ai',
        comments: record.comments?.startsWith('PERMANENT:')
          ? record.comments.substring(10)
          : record.comments
      })).filter(record => !isTeacher || recordOwnedByTeacher(record, currentUser));

      setAllRecords(processedData);

      // Extract unique values for filter dropdowns
      setUniqueTeachers([...new Set(processedData.map(r => r.teacher_name).filter(Boolean))].sort());
      setUniqueClasses([...new Set(processedData.map(r => r.class).filter(Boolean))].sort());
      setUniqueSubjects([...new Set(processedData.map(r => r.subject).filter(Boolean))].sort());
    } catch (err) {
      console.error('Error loading unmatched records:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load on mount
  useEffect(() => {
    loadUnmatchedRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply client-side filters. All dimensions are AND-combined, so picking
  // a calendar day AND a date range AND class/subject/teacher narrows the
  // result rather than replacing previous selections. Date comparisons use
  // the folder's test_date (the day the test was given), not created_at
  // (when the paper was checked). YYYY-MM-DD strings are compared as-is
  // so a date-only value does not shift a day in local time.
  useEffect(() => {
    let filtered = allRecords;
    if (filterMode === 'none' && !filterStartDate && !filterEndDate) {
      setUnmatchedRecords([]);
      return;
    }
    if (filterMode === 'date' && selectedDate) {
      filtered = filtered.filter(r => toLocalDayStr(r.test_date) === selectedDate);
    }
    if (filterStartDate) {
      filtered = filtered.filter(r => {
        const dayStr = toLocalDayStr(r.test_date);
        return dayStr && dayStr >= filterStartDate;
      });
    }
    if (filterEndDate) {
      filtered = filtered.filter(r => {
        const dayStr = toLocalDayStr(r.test_date);
        return dayStr && dayStr <= filterEndDate;
      });
    }
    if (filterStudent.trim()) {
      const needle = filterStudent.trim().toLowerCase();
      filtered = filtered.filter(r => (r.student_name || '').toLowerCase().includes(needle));
    }
    if (filterRubricType === 'mcq') {
      filtered = filtered.filter(isMcqRecord);
    } else if (filterRubricType === 'subjective') {
      filtered = filtered.filter(r => !isMcqRecord(r));
    }
    if (filterTeachers.length > 0) {
      const selectedTeachers = new Set(filterTeachers.map(f => f.value));
      filtered = filtered.filter(r => selectedTeachers.has(r.teacher_name));
    }
    if (filterClasses.length > 0) {
      const selectedClasses = new Set(filterClasses.map(f => f.value));
      filtered = filtered.filter(r => selectedClasses.has(r.class));
    }
    if (filterSubjects.length > 0) {
      const selectedSubjects = new Set(filterSubjects.map(f => f.value));
      filtered = filtered.filter(r => selectedSubjects.has(r.subject));
    }
    if (filterCause === 'all') {
      // keep all
    } else {
      filtered = filtered.filter(r => recordCause(r) === filterCause);
    }
    setUnmatchedRecords(filtered);
  }, [allRecords, filterMode, selectedDate, filterStartDate, filterEndDate, filterStudent, filterRubricType, filterCause, filterTeachers, filterClasses, filterSubjects]);

  const folderGroups = useMemo(() => {
    const map = new Map();
    for (const r of unmatchedRecords) {
      const key = mistakeFolderKey(r);
      let group = map.get(key);
      if (!group) {
        group = {
          key,
          title: mistakeFolderTitle(r),
          class: r.class,
          subject: r.subject,
          teacher_name: r.teacher_name || '',
          test_date: toLocalDayStr(r.test_date),
          isMcq: isMcqRecord(r),
          count: 0,
          studentCount: 0,
          aiCount: 0,
          scanCount: 0,
          sheetCount: 0
        };
        map.set(key, group);
      }
      group.count += 1;
      const cause = recordCause(r);
      if (cause === 'ai') group.aiCount += 1;
      else if (cause === 'scan') group.scanCount += 1;
      else if (cause === 'sheet') group.sheetCount += 1;
      else group.studentCount += 1;
    }
    return [...map.values()].sort((a, b) => {
      const da = a.test_date || '';
      const db = b.test_date || '';
      if (da !== db) return db.localeCompare(da);
      return a.title.localeCompare(b.title);
    });
  }, [unmatchedRecords]);

  const selectedFolder = useMemo(
    () => folderGroups.find(g => g.key === selectedFolderKey) || null,
    [folderGroups, selectedFolderKey]
  );

  const visibleRecords = useMemo(() => {
    if (!selectedFolderKey) return unmatchedRecords;
    return unmatchedRecords.filter(r => mistakeFolderKey(r) === selectedFolderKey);
  }, [unmatchedRecords, selectedFolderKey]);

  // If filters hide the open folder, bounce back to the folder list.
  useEffect(() => {
    if (!selectedFolderKey) return;
    if (!folderGroups.some(g => g.key === selectedFolderKey)) {
      setSelectedFolderKey(null);
    }
  }, [folderGroups, selectedFolderKey]);

  useEffect(() => {
    const onPop = (e) => {
      if (selectedFolderKey) {
        e.preventDefault();
        setSelectedFolderKey(null);
        window.history.pushState({ mistakesTab: true }, '');
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [selectedFolderKey]);

  const openFolder = (folder) => {
    setSelectedFolderKey(folder.key);
    setSelectedRecordIds(new Set());
    setPreviewImage(null);
    window.history.pushState({ mistakesFolder: folder.key }, '');
  };

  const closeFolder = () => {
    setSelectedFolderKey(null);
    setSelectedRecordIds(new Set());
  };

  useEffect(() => {
    const onMsg = (ev) => {
      if (ev.source !== window) return;
      const d = ev.data;
      if (!d || typeof d !== 'object' || d.source !== 'edupage-selector') return;
      if (d.type === 'EDU_BRIDGE_READY') {
        const features = Array.isArray(d.features) ? d.features : [];
        setEduBridgeReady(features.includes('SEND_MISTAKES') || d.version === '1.1.1' || !d.version);
      } else if (d.type === 'EDU_LOGIN_RESULT') {
        if (eduWaitRef.current) {
          clearTimeout(eduWaitRef.current);
          eduWaitRef.current = null;
        }
        if (d.ok) {
          setEduNotice(`Opening EduPage as ${d.username || 'this teacher'}…`);
          setError('');
        } else {
          setEduNotice('');
          setError(
            d.reason === 'not_found'
              ? "EduPage: this folder's teacher email isn't in the Login Edupage credential list."
              : 'EduPage send failed — reload Login Edupage in chrome://extensions, then refresh this page.'
          );
        }
      }
    };
    window.addEventListener('message', onMsg);
    window.postMessage({ source: 'fastgrader-edu', type: 'EDU_BRIDGE_PING' }, '*');
    return () => {
      window.removeEventListener('message', onMsg);
      if (eduWaitRef.current) clearTimeout(eduWaitRef.current);
    };
  }, []);

  const dispatchEduSend = (payload) => {
    const body = payload || eduPendingRef.current;
    if (!body) return;
    setError('');
    setEduNotice('Opening EduPage…');
    if (eduWaitRef.current) clearTimeout(eduWaitRef.current);
    eduWaitRef.current = setTimeout(() => {
      setEduNotice('');
      setError('Login Edupage did not respond. Open chrome://extensions, click Reload on Login Edupage, then refresh this page.');
    }, 2500);
    window.postMessage({ source: 'fastgrader-edu', type: 'EDU_BRIDGE_PING' }, '*');
    window.postMessage({
      source: 'fastgrader-edu',
      type: 'EDU_SEND_MISTAKES',
      teacher: body.teacher,
      courseName: body.courseName,
      className: body.className,
      subject: body.subject,
      rolls: body.rolls,
      message: body.message
    }, '*');
  };

  const handleSendMistakesToEduPage = () => {
    if (!selectedFolder) return;
    const records = pickMistakeRecords(visibleRecords, selectedRecordIds);
    if (!records.length) {
      setError('Nothing to send — only unmarked student mistakes go to EduPage (not AI, scan, or sheet).');
      return;
    }
    const { lines, rolls } = buildMistakePayload(records);
    if (!rolls.length || !lines.length) {
      setError('No student rolls to send — names must look like (B80) …');
      return;
    }
    const teacher = String(selectedFolder.teacher_name || currentUser?.username || '').trim();
    if (!teacher) {
      setError("This folder has no teacher email to log into EduPage with.");
      return;
    }
    const courseName = folderCourseName(selectedFolder);
    const message = formatEduMistakeMessage(selectedFolder, lines);
    const preview = message.split('\n').slice(0, 12).join('\n') + (lines.length > 4 ? `\n… +${Math.max(0, lines.length - 4)} more` : '');
    eduPendingRef.current = {
      teacher,
      courseName,
      className: selectedFolder.class || '',
      subject: selectedFolder.subject || '',
      rolls,
      message
    };
    showConfirm(
      `Send this list to ${rolls.length} student${rolls.length === 1 ? '' : 's'} on EduPage as ${teacher}?\n\n${preview}`,
      () => dispatchEduSend(eduPendingRef.current)
    );
  };

  const showConfirm = (text, callback) => setConfirm({ show: true, text, onConfirm: callback });
  const handleConfirm = () => {
    const fn = confirm.onConfirm;
    setConfirm({ show: false, text: '', onConfirm: null });
    try {
      if (typeof fn === 'function') fn();
      else if (eduPendingRef.current) dispatchEduSend(eduPendingRef.current);
    } catch (e) {
      setError('Failed to send to EduPage: ' + (e && e.message ? e.message : String(e)));
    }
  };
  const handleCancel = () => setConfirm({ show: false, text: '', onConfirm: null });

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  const handleDeleteRecord = async (recordId) => {
    showConfirm('Delete this unmatched record?', async () => {
      try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/unmatched-students/${recordId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const next = unmatchedRecords.filter(r => r.id !== recordId);
        setUnmatchedRecords(next);
        setAllRecords(prev => prev.filter(r => r.id !== recordId));
      } else {
        throw new Error('Failed to delete record');
      }
    } catch (err) {
      console.error('Error deleting record:', err);
      setError('Failed to delete record: ' + err.message);
    } finally {
      setLoading(false);
    }
    });
  };

  const handleDeleteAll = async () => {
    const ids = visibleRecords.map(r => r.id);
    if (ids.length === 0) return;
    const scope = selectedFolderKey
      ? `all ${ids.length} records in this folder`
      : `all ${ids.length} unmatched records`;
    showConfirm(`Delete ${scope}? This cannot be undone.`, async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('authToken');
        // Folder view: delete only the visible records. Folder-list "delete
        // all" (if re-enabled) still wipes the whole unmatched table.
        if (selectedFolderKey) {
          const results = await Promise.allSettled(ids.map(id => fetch(
            `${API_BASE_URL}/unmatched-students/${id}`,
            { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
          )));
          const deletedIds = new Set();
          let failures = 0;
          results.forEach((res, idx) => {
            if (res.status === 'fulfilled' && res.value && res.value.ok) {
              deletedIds.add(ids[idx]);
            } else {
              failures++;
            }
          });
          if (deletedIds.size > 0) {
            setUnmatchedRecords(prev => prev.filter(r => !deletedIds.has(r.id)));
            setAllRecords(prev => prev.filter(r => !deletedIds.has(r.id)));
          }
          clearSelection();
          if (failures > 0) {
            setError(`Deleted ${deletedIds.size}/${ids.length} — ${failures} failed. Please retry the rest.`);
          }
        } else {
          const response = await fetch(`${API_BASE_URL}/unmatched-students`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            setUnmatchedRecords([]);
            setAllRecords([]);
          } else {
            throw new Error('Failed to delete all records');
          }
        }
      } catch (err) {
        console.error('Error deleting all records:', err);
        setError('Failed to delete all records: ' + err.message);
      } finally {
        setLoading(false);
      }
    });
  };

  const clearFilters = () => {
    setSelectedDate('');
    setFilterMode('none');
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterStudent('');
    setFilterRubricType('all');
    setFilterCause('all');
    setFilterTeachers([]);
    setFilterClasses([]);
    setFilterSubjects([]);
    setSelectedFolderKey(null);
  };

  // Teacher / examiner / admin sets the cause: student (default), AI, scan,
  // or sheet. Buttons act as a radio group — student is selected until
  // another cause is chosen.
  const handleSetCause = async (record, cause) => {
    if (!canMarkAi || !record?.id) return;
    const current = recordCause(record);
    if (current === cause) return;
    const nextCause = cause;
    setMarkingAiIds(prev => {
      const next = new Set(prev);
      next.add(record.id);
      return next;
    });
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/unmatched-students/${record.id}/cause`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cause: nextCause })
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.message || `Failed to update (${response.status})`);
      }
      const updated = await response.json();
      const patch = {
        mistake_cause: updated.mistake_cause || nextCause,
        is_ai_mistake: Boolean(updated.is_ai_mistake),
        ai_marked_by: updated.ai_marked_by || null,
        ai_marked_at: updated.ai_marked_at || null
      };
      const apply = (r) => (r.id === record.id ? { ...r, ...patch } : r);
      setAllRecords(prev => prev.map(apply));
      setUnmatchedRecords(prev => prev.map(apply));
      setSelectedRecord(prev => (prev && prev.id === record.id ? { ...prev, ...patch } : prev));
    } catch (err) {
      console.error('Error setting mistake cause:', err);
      setError('Failed to mark mistake: ' + err.message);
    } finally {
      setMarkingAiIds(prev => {
        const next = new Set(prev);
        next.delete(record.id);
        return next;
      });
    }
  };

  const handleToggleAiMistake = (record) => handleSetCause(record, 'ai');

  // Drop any selections that are no longer visible (filter narrowed past
  // them). Avoids the grader hitting "Delete Selected" and silently nuking
  // records that aren't on screen.
  useEffect(() => {
    if (selectedRecordIds.size === 0) return;
    const visible = new Set(visibleRecords.map(r => r.id));
    let changed = false;
    const next = new Set();
    for (const id of selectedRecordIds) {
      if (visible.has(id)) next.add(id);
      else changed = true;
    }
    if (changed) setSelectedRecordIds(next);
  }, [visibleRecords, selectedRecordIds]);

  // Bulk delete every checked card. Fires DELETE in parallel — the
  // endpoint is idempotent so a partial failure leaves the rest gone but
  // logs an error rather than rolling back. After the wave settles we
  // refresh the local lists from the deleted set so the grid updates
  // without a full reload.
  const handleDeleteSelected = async () => {
    if (selectedRecordIds.size === 0) return;
    const ids = Array.from(selectedRecordIds);
    showConfirm(`Delete ${ids.length} selected record(s)? This cannot be undone.`, async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('authToken');
        const results = await Promise.allSettled(ids.map(id => fetch(
          `${API_BASE_URL}/unmatched-students/${id}`,
          { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
        )));
        const deletedIds = new Set();
        let failures = 0;
        results.forEach((res, idx) => {
          if (res.status === 'fulfilled' && res.value && res.value.ok) {
            deletedIds.add(ids[idx]);
          } else {
            failures++;
          }
        });
        if (deletedIds.size > 0) {
          setUnmatchedRecords(prev => prev.filter(r => !deletedIds.has(r.id)));
          setAllRecords(prev => prev.filter(r => !deletedIds.has(r.id)));
        }
        clearSelection();
        if (failures > 0) {
          setError(`Deleted ${deletedIds.size}/${ids.length} — ${failures} failed. Please retry the rest.`);
        }
      } catch (err) {
        console.error('Bulk delete failed:', err);
        setError('Bulk delete failed: ' + err.message);
      } finally {
        setLoading(false);
      }
    });
  };

  // Toggle "select all currently visible". Indeterminate (some selected)
  // collapses to "all selected" on the next click; "all selected" clears.
  const visibleIds = useMemo(() => visibleRecords.map(r => r.id), [visibleRecords]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedRecordIds.has(id));
  const someVisibleSelected = visibleIds.some(id => selectedRecordIds.has(id));
  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      // De-select only the visible ones, keep any selections from records
      // hidden by the current filter.
      setSelectedRecordIds(prev => {
        const next = new Set(prev);
        visibleIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedRecordIds(prev => {
        const next = new Set(prev);
        visibleIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  // Extract image filename from comments. Supports:
  //   [Image_XXXXX.jpg]  — name-mismatch / MCQ tags
  //   Image: Image_XXXXX.jpg  — invalid-roll auto-sort logs (no brackets)
  const extractImageName = (comments) => {
    if (!comments) return null;
    const bracket = comments.match(/\[([^\]]+\.(?:jpg|jpeg|png|gif))\]/i);
    if (bracket) return bracket[1];
    const plain = comments.match(/Image:\s*([^\s,\]]+\.(?:jpg|jpeg|png|gif))/i);
    return plain ? plain[1] : null;
  };

  // Extract a scan-id fallback from comments (pattern: [scan:NNN]). MCQs
  // OMR + Approve & Match drop this tag when the row was reloaded from DB
  // without an attached filename — the scan id is the only way back to the
  // image at that point. Resolves through /scans/file/:scanId/raw (image
  // bytes, not a presigned URL) so we wrap it as a blob: URL on the client.
  const extractScanId = (comments) => {
    if (!comments) return null;
    const match = comments.match(/\[scan:(\d+)\]/i);
    return match ? match[1] : null;
  };

  // Single key used by the URL cache + the inline thumbnail. Scan-id refs
  // win when both are present — filenames collide (the scanner produces
  // sequential "Image_NNN.jpg" names that recur across folders/uploads),
  // so /scans/by-filename can return a different scan than the one that
  // actually triggered the mistake. scan_id is the only globally-unique
  // identifier. Falls back to the filename tag for older records that
  // pre-date this change.
  const extractImageRef = (comments) => {
    const id = extractScanId(comments);
    if (id) return { kind: 'scan', value: id };
    const name = extractImageName(comments);
    if (name) return { kind: 'name', value: name };
    return null;
  };

  // Shared filename → presigned-URL cache for the inline card thumbnails AND
  // the full-screen preview modal. Lives in a ref Map for stable identity;
  // we tick urlCacheVersion to nudge consumers to re-render when an entry
  // changes. Using a ref instead of a state Map means InlineMistakeImage's
  // fetchUrl callback is referentially stable, so its useEffect doesn't loop
  // on each cache update.
  const imageUrlCacheRef = useRef(new Map());
  const [urlCacheVersion, setUrlCacheVersion] = useState(0);
  const bumpUrlCache = () => setUrlCacheVersion(v => v + 1);

  // Cache key for a ref — separates filename-based and scan-id-based refs
  // so they never collide (different transport paths, different lifetimes).
  const refKey = (ref) => ref ? `${ref.kind}:${ref.value}` : null;

  const fetchImageUrl = React.useCallback(async (ref) => {
    const key = refKey(ref);
    if (!key) return;
    const cache = imageUrlCacheRef.current;
    const existing = cache.get(key);
    if (existing && (existing.url || existing.error || existing.loading)) return;
    cache.set(key, { loading: true });
    bumpUrlCache();
    try {
      const token = localStorage.getItem('authToken');
      if (ref.kind === 'name') {
        // /scans/by-filename returns a JSON envelope with a presigned URL.
        const response = await fetch(
          `${API_BASE_URL}/scans/by-filename/${encodeURIComponent(ref.value)}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const data = await response.json();
        if (data.success && data.downloadUrl) {
          cache.set(key, { url: data.downloadUrl });
        } else {
          cache.set(key, { error: 'Not found' });
        }
      } else {
        // /scans/file/:id/raw returns the raw bytes — wrap as blob: URL so
        // the <img> tag can render it. Same path MCQImagePreview uses to
        // keep canvases untainted by cross-origin S3.
        const response = await fetch(
          `${API_BASE_URL}/scans/file/${encodeURIComponent(ref.value)}/raw`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!response.ok) {
          cache.set(key, { error: 'HTTP ' + response.status });
        } else {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          cache.set(key, { url: blobUrl, blob: true });
        }
      }
    } catch (err) {
      console.warn('[MistakesTab] thumbnail fetch failed:', err);
      cache.set(key, { error: err.message || 'Fetch failed' });
    } finally {
      bumpUrlCache();
    }
  }, []);

  // Revoke any blob: URLs we created on unmount so they don't pile up.
  useEffect(() => () => {
    const cache = imageUrlCacheRef.current;
    cache.forEach((entry) => {
      if (entry && entry.blob && entry.url) URL.revokeObjectURL(entry.url);
    });
  }, []);

  // Preview image from scans — used by the existing image button + by clicks
  // on the new inline card thumbnails. Reuses the shared URL cache so the
  // modal opens instantly when the thumbnail is already loaded.
  // `recordId` lets ←/→ step through other mistakes that also have images.
  const handlePreviewImage = async (comments, recordId = null) => {
    const ref = extractImageRef(comments);
    if (!ref) return;
    const key = refKey(ref);
    const cached = imageUrlCacheRef.current.get(key);
    if (cached && cached.url) {
      setPreviewImage({ url: cached.url, name: ref.value, recordId });
      return;
    }
    setPreviewLoading(key);
    await fetchImageUrl(ref);
    const after = imageUrlCacheRef.current.get(key);
    if (after && after.url) {
      setPreviewImage({ url: after.url, name: ref.value, recordId });
    } else {
      setError('Image not found: ' + ref.value);
    }
    setPreviewLoading(null);
  };

  // Currently-filtered records that have a resolvable image reference —
  // the ←/→ sequence follows this list (same order as the card grid).
  const previewableRecords = useMemo(
    () => visibleRecords.filter(r => extractImageRef(r.comments)),
    // extractImageRef is stable in behavior; recompute when the filtered set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleRecords]
  );

  // Arrow-key gallery while the image panel is open. Skips when focus is in
  // an input so filters / dialogs keep their normal arrow behavior.
  useEffect(() => {
    if (!previewImage) return;
    const isEditable = (el) =>
      !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
    const onKey = (e) => {
      if (isEditable(e.target)) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const list = previewableRecords;
      if (list.length === 0) return;
      let idx = list.findIndex(r => r.id === previewImage.recordId);
      // If we opened without a recordId (shouldn't happen), start from ends.
      if (idx < 0) idx = e.key === 'ArrowRight' ? -1 : 0;
      const nextIdx = e.key === 'ArrowRight' ? idx + 1 : idx - 1;
      if (nextIdx < 0 || nextIdx >= list.length) return;
      const next = list[nextIdx];
      handlePreviewImage(next.comments, next.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // handlePreviewImage closes over latest cache; listing deps on preview
    // state + filtered list is enough to rebind when the gallery changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewImage, previewableRecords]);

  // Shared react-select option style block — keeps Teacher / Class / Subject
  // filters identical in look. Compact rows (4px vertical padding) so the
  // dropdown packs many courses. Menu is nearly full-page height so a long
  // class list can be scanned without a tiny scroll window.
  const compactSelectStyles = (active) => ({
    control: (base) => ({ ...base, borderRadius: 8, fontSize: '0.85rem', minHeight: 38, borderColor: active ? '#3b82f6' : '#ddd' }),
    menu: (base) => ({ ...base, zIndex: 10000 }),
    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
    menuList: (base) => ({
      ...base,
      maxHeight: 'calc(100vh - 140px)',
    }),
    option: (base, state) => ({
      ...base,
      background: state.isFocused ? '#f0f4ff' : 'white',
      color: '#111827',
      cursor: 'pointer',
      padding: '4px 12px',
      ':active': { background: '#e0e7ff' }
    }),
    placeholder: (base) => ({ ...base, color: active ? '#3b82f6' : base.color })
  });
  const filterSelectMenuProps = {
    maxMenuHeight: typeof window !== 'undefined' ? window.innerHeight - 140 : 800,
    menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
    menuShouldScrollIntoView: false,
  };

  return (
    <div className="mistakes-tab">
      {/* Top header — mirrors ScansTab: 40×40 icon chip on the left, title /
          subtitle next to it, Class / Subject filters + Clear + Delete-All on
          the right of the same row. */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
          }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ color: 'white', fontSize: '20px' }}></i>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            {selectedFolder ? (
              <>
                <button
                  type="button"
                  onClick={closeFolder}
                  className="mistakes-back-btn"
                  title="Back to folder list"
                >
                  <i className="fa-solid fa-arrow-left"></i>
                  Folders
                </button>
                <h1 style={{ color: 'var(--text-primary)', margin: '6px 0 0 0', fontSize: '1.125rem', fontWeight: 700 }}>
                  {selectedFolder.title}
                  <span style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                    {' '}({visibleRecords.length})
                  </span>
                </h1>
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  {formatFolderDate(selectedFolder.test_date)}
                  {selectedFolder.teacher_name ? ` · ${selectedFolder.teacher_name}` : ''}
                  {selectedFolder.isMcq ? ' · MCQs' : ''}
                </p>
              </>
            ) : (
              <>
                <h1 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '1.125rem', fontWeight: 700 }}>
                  Mistake folders ({folderGroups.length})
                </h1>
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  {unmatchedRecords.length} unmatched record{unmatchedRecords.length !== 1 ? 's' : ''} — click a folder to open it
                </p>
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            {/* Student-name filter — case-insensitive substring match on
                student_name. Free-text input rather than a dropdown
                because the student count is open-ended and people search
                by partial roll codes like "BD4". */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 200 }}>
              <i className="fa-solid fa-user-graduate" style={{ color: '#0EA5E9', fontSize: '0.9375rem' }}></i>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)' }}>Student:</label>
              <input
                type="text"
                value={filterStudent}
                onChange={(e) => setFilterStudent(e.target.value)}
                placeholder="Filter by name"
                style={{
                  flex: 1,
                  minWidth: 140,
                  padding: '7px 10px',
                  border: `1px solid ${filterStudent.trim() ? '#3b82f6' : 'var(--border, #d1d5db)'}`,
                  borderRadius: 8,
                  fontSize: 13,
                  background: 'var(--surface, white)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>

            {/* Teacher filter — scans-folder owner. Hidden for teachers:
                they only see folders they created. */}
            {!isTeacher && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 200 }}>
              <i className="fa-solid fa-user-tie" style={{ color: '#8B5CF6', fontSize: '0.9375rem' }}></i>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)' }}>Teacher:</label>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Select
                  value={filterTeachers}
                  onChange={(selected) => setFilterTeachers(selected || [])}
                  inputValue={teacherFilterInput}
                  onInputChange={(val, action) => { if (action.action === 'input-change') setTeacherFilterInput(val); }}
                  options={uniqueTeachers.map(t => ({ value: t, label: t }))}
                  isMulti
                  isSearchable
                  isClearable
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  controlShouldRenderValue={false}
                  {...filterSelectMenuProps}
                  placeholder={filterTeachers.length > 0 ? `${filterTeachers.length} selected` : 'All Teachers'}
                  noOptionsMessage={() => 'No teachers'}
                  formatOptionLabel={(option, { context }) => {
                    if (context === 'value') return <span>{option.label}</span>;
                    const isChecked = filterTeachers.some(f => f.value === option.value);
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input type="checkbox" checked={isChecked} readOnly style={{ cursor: 'pointer', accentColor: '#3b82f6' }} />
                        <span>{option.label}</span>
                      </div>
                    );
                  }}
                  styles={compactSelectStyles(filterTeachers.length > 0)}
                />
              </div>
            </div>
            )}

            {/* Class filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 200 }}>
              <i className="fa-solid fa-graduation-cap" style={{ color: '#3B82F6', fontSize: '0.9375rem' }}></i>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)' }}>Class:</label>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Select
                  value={filterClasses}
                  onChange={(selected) => setFilterClasses(selected || [])}
                  inputValue={classFilterInput}
                  onInputChange={(val, action) => { if (action.action === 'input-change') setClassFilterInput(val); }}
                  options={uniqueClasses.map(cls => ({ value: cls, label: cls }))}
                  isMulti
                  isSearchable
                  isClearable
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  controlShouldRenderValue={false}
                  {...filterSelectMenuProps}
                  placeholder={filterClasses.length > 0 ? `${filterClasses.length} selected` : 'All Classes'}
                  noOptionsMessage={() => 'No classes'}
                  formatOptionLabel={(option, { context }) => {
                    if (context === 'value') return <span>{option.label}</span>;
                    const isChecked = filterClasses.some(f => f.value === option.value);
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input type="checkbox" checked={isChecked} readOnly style={{ cursor: 'pointer', accentColor: '#3b82f6' }} />
                        <span>{option.label}</span>
                      </div>
                    );
                  }}
                  styles={compactSelectStyles(filterClasses.length > 0)}
                />
              </div>
            </div>

            {/* Subject filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 200 }}>
              <i className="fa-solid fa-book" style={{ color: '#10b981', fontSize: '0.9375rem' }}></i>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)' }}>Subject:</label>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Select
                  value={filterSubjects}
                  onChange={(selected) => setFilterSubjects(selected || [])}
                  inputValue={subjectFilterInput}
                  onInputChange={(val, action) => { if (action.action === 'input-change') setSubjectFilterInput(val); }}
                  options={uniqueSubjects.map(subj => ({ value: subj, label: subj }))}
                  isMulti
                  isSearchable
                  isClearable
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  controlShouldRenderValue={false}
                  {...filterSelectMenuProps}
                  placeholder={filterSubjects.length > 0 ? `${filterSubjects.length} selected` : 'All Subjects'}
                  noOptionsMessage={() => 'No subjects'}
                  formatOptionLabel={(option, { context }) => {
                    if (context === 'value') return <span>{option.label}</span>;
                    const isChecked = filterSubjects.some(f => f.value === option.value);
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input type="checkbox" checked={isChecked} readOnly style={{ cursor: 'pointer', accentColor: '#3b82f6' }} />
                        <span>{option.label}</span>
                      </div>
                    );
                  }}
                  styles={compactSelectStyles(filterSubjects.length > 0)}
                />
              </div>
            </div>

            {(filterMode === 'date' || filterStartDate || filterEndDate || filterStudent.trim() || filterRubricType !== 'all' || filterCause !== 'all' || filterTeachers.length > 0 || filterClasses.length > 0 || filterSubjects.length > 0) && (
              <button
                onClick={clearFilters}
                style={{
                  padding: '8px 14px',
                  border: 'none',
                  borderRadius: 8,
                  background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: '0 2px 6px rgba(245, 158, 11, 0.3)'
                }}
              >
                <i className="fas fa-filter-circle-xmark"></i>
                Clear
              </button>
            )}

            {/* Bulk-select controls — visible only when there are records to
                act on. "Select all" toggles the checkboxes on every visible
                card; "Delete Selected" wipes whatever is currently
                checked. Both stay in the same header strip as Delete All so
                the destructive actions are grouped. */}
            {selectedFolderKey && visibleRecords.length > 0 && (
              <label
                title="Select / deselect every card currently shown"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 12px', borderRadius: 8,
                  border: `1px solid ${someVisibleSelected ? '#3b82f6' : 'var(--border, #d1d5db)'}`,
                  background: someVisibleSelected ? 'rgba(59,130,246,0.08)' : 'var(--surface, white)',
                  cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  color: 'var(--text-primary)'
                }}
              >
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  ref={el => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                  onChange={toggleSelectAllVisible}
                  style={{ cursor: 'pointer', accentColor: '#3b82f6', margin: 0 }}
                />
                Select all
              </label>
            )}

            {selectedRecordIds.size > 0 && (
              <button
                onClick={handleDeleteSelected}
                disabled={loading}
                title="Delete every checked card"
                style={{
                  padding: '8px 14px',
                  border: 'none',
                  borderRadius: 8,
                  background: 'linear-gradient(135deg, #DC2626, #991B1B)',
                  color: 'white',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: '0 2px 6px rgba(220, 38, 38, 0.3)'
                }}
              >
                <i className="fa-solid fa-trash"></i>
                Delete Selected ({selectedRecordIds.size})
              </button>
            )}

            {selectedFolderKey && visibleRecords.length > 0 && (
              <button
                type="button"
                className="btn-edupage-send"
                onClick={handleSendMistakesToEduPage}
                disabled={loading}
                title={eduBridgeReady
                  ? 'Log into this teacher on EduPage, pick the students, paste the list, then Send message'
                  : 'Needs the Login Edupage extension (reload unpacked v1.1.0+)'}
              >
                <i className="fa-solid fa-paper-plane"></i>
                Send on EduPage
              </button>
            )}

            {selectedFolderKey && visibleRecords.length > 0 && (
              <button
                className="btn-delete-all"
                onClick={handleDeleteAll}
                disabled={loading}
                title="Delete all unmatched records"
              >
                <i className="fa-solid fa-trash"></i> Delete All ({visibleRecords.length})
              </button>
            )}

            <button
              type="button"
              onClick={() => setCalendarCollapsed(c => !c)}
              title={calendarCollapsed
                ? 'Show the calendar, date range, and type filters'
                : 'Hide the calendar panel to give the records grid more room'}
              style={{
                marginLeft: 'auto',
                padding: '8px 12px',
                border: '1px solid var(--border, #d1d5db)',
                borderRadius: 8,
                background: 'var(--surface, white)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                whiteSpace: 'nowrap'
              }}
            >
              <i className={`fa-solid fa-${calendarCollapsed ? 'eye' : 'eye-slash'}`}></i>
              {calendarCollapsed ? 'Show calendar' : 'Hide calendar'}
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {eduNotice && <div className="edu-notice">{eduNotice}</div>}
      {error && <div className="error-message">{error}</div>}

      {/* Loading State */}
      {loading && <div className="loading-message">Loading records...</div>}

      {/* Two-column body — mirrors ScansTab / SavedRubrics: narrow LEFT rail
          holds a 30-day calendar that filters records by the folder's
          test_date; the
          RIGHT pane fills with the records grid. Stacks on narrow viewports
          via flexWrap. */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {!calendarCollapsed && (
        <div style={{ width: 360, flexShrink: 0 }}>
          <DateFilterCalendar
            items={allRecords}
            dateField="test_date"
            selectedDate={filterMode === 'date' ? selectedDate : ''}
            onDateChange={(dateStr) => {
              if (dateStr) {
                setSelectedDate(dateStr);
                setFilterMode('date');
              } else {
                setSelectedDate('');
                setFilterMode('none');
              }
            }}
            itemLabelSingular="mis"
            itemLabelPlural="mis"
            itemLabelSeparator="-"
            title="Select Date"
            columns={5}
          />

          {/* Custom date range — anchored below the calendar for cases the
              30-day rail can't cover (older records, multi-month spans).
              ANDs with the calendar pick, so leaving the calendar on "all"
              lets the range drive the filter on its own. */}
          <div className="filters-section" style={{ marginTop: 12 }}>
            <h3>Select Date Range</h3>
            <div className="filter-group" style={{ marginBottom: 10 }}>
              <label>From Date:</label>
              <input
                type="date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>To Date:</label>
              <input
                type="date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
              />
            </div>
          </div>

          {/* Rubric-type segmented control — "MCQs" keeps records whose
              subject ends in an MCQ variant letter (-a, -b, …, -j),
              "Subjective" keeps the rest. Visual treatment mirrors the
              All / Printed / Not-Printed strip on ScansTab so the rail
              feels consistent across tabs. */}
          <div style={{
            display: 'inline-flex',
            border: '1px solid var(--border)',
            borderRadius: 8,
            overflow: 'hidden',
            background: 'var(--surface)',
            marginTop: 12,
            width: '100%'
          }}>
            {[
              { v: 'all',         label: 'All' },
              { v: 'mcq',         label: 'MCQs' },
              { v: 'subjective',  label: 'Subjective' }
            ].map(opt => (
              <button
                key={opt.v}
                onClick={() => setFilterRubricType(opt.v)}
                style={{
                  flex: 1,
                  padding: '7px 10px',
                  border: 'none',
                  background: filterRubricType === opt.v
                    ? 'linear-gradient(135deg, #8B5CF6, #6D28D9)'
                    : 'transparent',
                  color: filterRubricType === opt.v ? 'white' : 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 700,
                  whiteSpace: 'nowrap'
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Cause filter — Student / AI / Scan / Sheet. Default unmarked
              records are student mistakes. */}
          <div style={{
            display: 'inline-flex',
            border: '1px solid var(--border)',
            borderRadius: 8,
            overflow: 'hidden',
            background: 'var(--surface)',
            marginTop: 12,
            width: '100%',
            flexWrap: 'wrap'
          }}>
            {[
              { v: 'all',     label: 'All' },
              { v: 'student', label: 'Student' },
              { v: 'ai',      label: 'AI' },
              { v: 'scan',    label: 'Scan' },
              { v: 'sheet',   label: 'Sheet' }
            ].map(opt => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setFilterCause(opt.v)}
                title={opt.v === 'all'
                  ? 'Show every record'
                  : `Show only ${opt.label.toLowerCase()} mistakes`}
                style={{
                  flex: 1,
                  minWidth: 56,
                  padding: '7px 8px',
                  border: 'none',
                  background: filterCause === opt.v
                    ? (opt.v === 'ai'
                      ? 'linear-gradient(135deg, #6366F1, #4338CA)'
                      : opt.v === 'scan'
                        ? 'linear-gradient(135deg, #F59E0B, #D97706)'
                        : opt.v === 'sheet'
                          ? 'linear-gradient(135deg, #10B981, #047857)'
                          : opt.v === 'student'
                            ? 'linear-gradient(135deg, #0EA5E9, #0369A1)'
                            : 'linear-gradient(135deg, #8B5CF6, #6D28D9)')
                    : 'transparent',
                  color: filterCause === opt.v ? 'white' : 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 700,
                  whiteSpace: 'nowrap'
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Top-20 most-frequent-mistake students — opens a modal with a
              leaderboard derived from the currently-filtered records, so
              picking a class/teacher first scopes the ranking to that
              slice. Always reflects what's visible in the grid. */}
          <button
            onClick={() => setShowTopOffendersModal(true)}
            disabled={visibleRecords.length === 0}
            title="Show the 20 students with the most unmatched records (respects current filters)"
            style={{
              marginTop: 12,
              width: '100%',
              padding: '10px 14px',
              border: 'none',
              borderRadius: 8,
              cursor: visibleRecords.length === 0 ? 'not-allowed' : 'pointer',
              background: visibleRecords.length === 0
                ? '#ccc'
                : 'linear-gradient(135deg, #F59E0B, #D97706)',
              color: 'white',
              fontSize: 13,
              fontWeight: 700,
              boxShadow: visibleRecords.length === 0 ? 'none' : '0 2px 6px rgba(245, 158, 11, 0.3)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8
            }}
          >
            <i className="fa-solid fa-ranking-star"></i>
            Top 20 Repeat Offenders
          </button>
        </div>
        )}

        {(filterMode !== 'none' || filterStartDate || filterEndDate) && (
        <div style={{ flex: 1, minWidth: 0 }}>
          {!loading && unmatchedRecords.length === 0 && (
            <div className="no-records">
              <p>
                {filterCause === 'ai'
                  ? 'No records marked as AI mistakes.'
                  : filterCause === 'scan'
                    ? 'No records marked as scan mistakes.'
                    : filterCause === 'sheet'
                      ? 'No records marked as sheet mistakes.'
                      : filterCause === 'student'
                        ? 'No student mistakes in this filter.'
                        : 'No unmatched records found.'}
              </p>
            </div>
          )}

          {!loading && !selectedFolderKey && unmatchedRecords.length > 0 && (
            <div className="mistake-folders-grid">
              {folderGroups.map((folder) => (
                <button
                  type="button"
                  key={folder.key}
                  className="mistake-folder-card"
                  onClick={() => openFolder(folder)}
                  title={`Open mistakes for ${folder.title}`}
                >
                  <div className="mistake-folder-icon">
                    <i className="fa-solid fa-folder"></i>
                  </div>
                  <div className="mistake-folder-info">
                    <h3 className="mistake-folder-title">
                      {folder.title}
                      {folder.isMcq ? <span className="mistake-folder-mcq">MCQs</span> : null}
                    </h3>
                    <div className="mistake-folder-meta">
                      <span>{formatFolderDate(folder.test_date)}</span>
                      {folder.teacher_name ? <span>{folder.teacher_name}</span> : null}
                    </div>
                    <div className="mistake-folder-count">
                      {folder.count} {folder.count === 1 ? 'mistake' : 'mistakes'}
                      {folder.aiCount > 0 ? ` · ${folder.aiCount} AI` : ''}
                      {folder.scanCount > 0 ? ` · ${folder.scanCount} scan` : ''}
                      {folder.sheetCount > 0 ? ` · ${folder.sheetCount} sheet` : ''}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {!loading && selectedFolderKey && visibleRecords.length === 0 && unmatchedRecords.length > 0 && (
            <div className="no-records">
              <p>No mistakes in this folder for the current filters.</p>
            </div>
          )}

          {!loading && selectedFolderKey && visibleRecords.length > 0 && (
            <div className="records-grid">
              {visibleRecords.map((record) => {
                const isSelected = selectedRecordIds.has(record.id);
                const cause = recordCause(record);
                const isMarkingAi = markingAiIds.has(record.id);
                return (
            <div
              key={record.id}
              className={`record-card${cause !== 'student' ? ` record-card--${cause}` : ''}`}
              style={isSelected ? { boxShadow: '0 0 0 2px #3b82f6', position: 'relative' } : { position: 'relative' }}
            >
              {/* Multi-select checkbox — pinned top-right of the card so it
                  doesn't crowd the header text. The whole card body still
                  responds to its existing buttons; only the checkbox area
                  toggles selection. */}
              <label
                onClick={(e) => e.stopPropagation()}
                title={isSelected ? 'Deselect' : 'Select for bulk delete'}
                style={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  zIndex: 2,
                  width: 22,
                  height: 22,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,0.92)',
                  border: '1px solid var(--border, #d1d5db)',
                  borderRadius: 4,
                  cursor: 'pointer',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleRecordSelected(record.id)}
                  style={{ cursor: 'pointer', accentColor: '#3b82f6', margin: 0 }}
                />
              </label>
              {/* Card Header — student name only. Class/subject is already
                  the folder title above, so repeating "8B-B computer-a" on
                  every card is noise. */}
              <div className="card-header">
                <NameDiffLine
                  readName={(record.student_name || '').trim()}
                  expected={extractExpectedName(record.comments)}
                />
              </div>

              {/* Top-20% scan preview in place of Created by / Created At
                  (those stay in the Details modal). */}
              <InlineMistakeImage
                imageRef={extractImageRef(record.comments)}
                cacheRef={imageUrlCacheRef}
                cacheVersion={urlCacheVersion}
                fetchImageUrl={fetchImageUrl}
                onOpen={() => handlePreviewImage(record.comments, record.id)}
              />

              {/* Actions — cause marks only (details / image / delete removed from card) */}
              <div className="card-actions">
                {canMarkAi && CAUSE_MARKS.map((m) => {
                  const on = cause === m.cause;
                  return (
                    <button
                      key={m.cause}
                      type="button"
                      className={`btn-cause-mark btn-cause-mark--${m.cause}${on ? ' is-on' : ''}`}
                      onClick={() => handleSetCause(record, m.cause)}
                      disabled={isMarkingAi}
                      title={on ? m.titleOn : m.titleOff}
                      aria-label={on ? m.titleOn : m.titleOff}
                    >
                      <i className={`fa-solid ${m.icon}`}></i>
                      <span className="btn-cause-mark__label">{m.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
                );
              })}
        </div>
      )}
        </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedRecord && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Student Details</h3>
              <button
                className="btn-close"
                onClick={() => setShowDetailModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h4>Student Information</h4>
                <p><strong>Name:</strong> {selectedRecord.student_name}</p>
                {extractExpectedName(selectedRecord.comments) && (
                  <p><strong>Expected:</strong> {extractExpectedName(selectedRecord.comments)}</p>
                )}
                <p><strong>Marks:</strong> {selectedRecord.marks || 'N/A'}</p>
                <p>
                  <strong>Cause:</strong>{' '}
                  <span className={`cause-badge ${(CAUSE_BADGE[recordCause(selectedRecord)] || CAUSE_BADGE.student).className}`}>
                    {(CAUSE_BADGE[recordCause(selectedRecord)] || CAUSE_BADGE.student).text}
                  </span>
                </p>
                {recordCause(selectedRecord) !== 'student' && selectedRecord.ai_marked_by && (
                  <p><strong>Marked by:</strong> {selectedRecord.ai_marked_by}</p>
                )}
                {canMarkAi && (
                  <div className="cause-mark-row" style={{ marginTop: 8 }}>
                    {CAUSE_MARKS.map((m) => {
                      const on = recordCause(selectedRecord) === m.cause;
                      return (
                        <button
                          key={m.cause}
                          type="button"
                          className={`btn-cause-mark btn-cause-mark--${m.cause}${on ? ' is-on' : ''}`}
                          onClick={() => handleSetCause(selectedRecord, m.cause)}
                          disabled={markingAiIds.has(selectedRecord.id)}
                          title={on ? m.titleOn : m.titleOff}
                        >
                          <i className={`fa-solid ${m.icon}`}></i>
                          {m.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="detail-section">
                <h4>List Information</h4>
                <p><strong>Class:</strong> {selectedRecord.class}</p>
                <p><strong>Section:</strong> {selectedRecord.section || 'N/A'}</p>
                <p><strong>Subject:</strong> {selectedRecord.subject}</p>
              </div>

              <div className="detail-section">
                <h4>Comments</h4>
                <div className="comments-box">
                  {selectedRecord.comments || 'No comments'}
                </div>
              </div>

              <div className="detail-section">
                <h4>Record Information</h4>
                <p><strong>Teacher:</strong> {selectedRecord.teacher_name || 'N/A'}</p>
                <p><strong>Checked by:</strong> {selectedRecord.created_by}</p>
                <p><strong>Test date:</strong> {selectedRecord.test_date || 'N/A'}</p>
                <p><strong>Checked at:</strong> {formatToPakistanTime(selectedRecord.created_at)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top-20 Repeat-Offenders Modal — groups the currently-filtered
          records by student_name, sorts by count desc, and shows the top
          20. Clicking a row sets the Student filter so the user can drill
          straight from the leaderboard into that student's records. */}
      {showTopOffendersModal && (() => {
        const counts = new Map();
        for (const r of visibleRecords) {
          const name = (r.student_name || '').trim();
          if (!name) continue;
          counts.set(name, (counts.get(name) || 0) + 1);
        }
        const ranked = [...counts.entries()]
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
          .slice(0, 20);

        return (
          <div
            onClick={() => setShowTopOffendersModal(false)}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 2000, padding: 'calc(var(--header-height, 52px) + 8px) 20px 20px',
              boxSizing: 'border-box'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'var(--panel-bg, white)', borderRadius: '16px', padding: '20px',
                maxWidth: '560px', width: '100%', maxHeight: '85vh', overflow: 'auto',
                boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                  <i className="fa-solid fa-ranking-star" style={{ color: '#F59E0B', marginRight: '8px' }}></i>
                  Top {ranked.length} Repeat Offenders
                </h3>
                <button
                  onClick={() => setShowTopOffendersModal(false)}
                  style={{ background: '#EF4444', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' }}
                >
                  <i className="fa-solid fa-times"></i>
                </button>
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0 0 12px 0' }}>
                Ranked by unmatched-record count across the currently-filtered set
                ({visibleRecords.length} record{visibleRecords.length !== 1 ? 's' : ''}).
                Click a row to filter the grid by that student.
              </p>
              {ranked.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  No named students in the current set.
                </div>
              ) : (
                <div style={{ border: '1px solid var(--border-color, #e0e0e0)', borderRadius: '8px', overflow: 'hidden' }}>
                  {ranked.map((row, idx) => (
                    <div
                      key={row.name}
                      onClick={() => {
                        setFilterStudent(row.name);
                        setShowTopOffendersModal(false);
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '6px 12px', cursor: 'pointer',
                        background: idx === 0
                          ? 'rgba(245, 158, 11, 0.12)'
                          : (idx % 2 === 0 ? 'var(--panel-bg, white)' : 'var(--surface, #f9fafb)'),
                        borderBottom: idx < ranked.length - 1 ? '1px solid var(--border-color, #f0f0f0)' : 'none'
                      }}
                    >
                      <span style={{
                        minWidth: 28, textAlign: 'right', fontWeight: 700, fontSize: '0.85rem',
                        color: idx === 0 ? '#D97706' : 'var(--text-secondary)'
                      }}>
                        {idx + 1}.
                      </span>
                      <span style={{ flex: 1, fontWeight: 600, fontSize: '0.88rem', color: 'var(--text-primary)' }}>
                        {row.name}
                      </span>
                      <span style={{
                        background: idx === 0
                          ? 'linear-gradient(135deg, #F59E0B, #D97706)'
                          : 'var(--surface, #f3f4f6)',
                        color: idx === 0 ? 'white' : 'var(--text-primary)',
                        padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 700,
                        whiteSpace: 'nowrap'
                      }}>
                        {row.count} {row.count === 1 ? 'mistake' : 'mistakes'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Portrait panel on the left — top 20% of the page (name / roll).
          ← / → steps through other filtered mistakes that have images. */}
      {previewImage && (
        <DraggableImagePanel
          key={previewImage.recordId || previewImage.url}
          url={previewImage.url}
          name={(() => {
            const list = previewableRecords;
            const idx = list.findIndex(r => r.id === previewImage.recordId);
            const pos = idx >= 0 ? ` (${idx + 1}/${list.length})` : '';
            return `${previewImage.name}${pos}`;
          })()}
          onClose={() => setPreviewImage(null)}
          anchor="left"
          width="36vw"
          imageHeight="78vh"
          initialFocus="top20"
          initialZoom={2.04}
          defaultTool="hand"
          availableTools={['hand', 'rect', 'eraser']}
          fixedStrokeWidth={5}
        />
      )}

      <ConfirmDialog 
        confirm={confirm} 
        onConfirm={handleConfirm} 
        onCancel={handleCancel} 
      />
    </div>
  );
}
